import { MouseEventHandler, ReactNode } from 'react';
import styled from 'styled-components';

import { IconCircle } from '../icons/cdl/Circle';
import { IconCircleCheck } from '../icons/cdl/CircleCheck';
import { Box, BoxProps } from '../ui/Box';
import { theme } from '../ui/theme';

interface SelectBoxProps extends Pick<BoxProps, 'height' | 'width' | 'padding'> {
    children: ReactNode;
    selected?: boolean;
    disabled?: boolean;
    onClick?: MouseEventHandler;
}

const getBorderBoxShadow = (props: SelectBoxProps) => {
    let boxShadowColor: string = theme.colors.border.default;

    if (props.selected) {
        boxShadowColor = theme.colors.accent.foreground;
    }

    if (props.disabled) {
        boxShadowColor = theme.colors.border.subtle;
    }

    const borderShadow = props.selected ? `0 0 0 2px ${boxShadowColor}` : `0 0 0 1px ${boxShadowColor}`;

    const boxShadow = props.selected ? theme.shadows.large : theme.shadows.medium;

    return `${borderShadow}, ${boxShadow}`;
};

const IconWrapper = styled.div<Pick<SelectBoxProps, 'selected' | 'disabled'>>`
    position: absolute;
    top: 8px;
    right: 8px;
    transition: color 100ms;
    color: ${(props) => (props.selected ? theme.colors.accent.foreground : theme.colors.foreground.subtle)};
    color: ${(props) => (props.disabled ? theme.colors.foreground.disabled : null)};
`;

const ClickableBox = styled(Box)<SelectBoxProps>`
    width: ${(props) => (props.width ? props.width : '100%')};
    height: ${(props) => (props.height ? props.height : '100%')};
    text-align: left;
    white-space: normal;
    background-color: ${theme.colors.background.default};
    color: ${(props) => (props.disabled ? theme.colors.foreground.disabled : theme.colors.foreground.default)};
    position: relative;
    border-radius: 4px;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    box-shadow: ${getBorderBoxShadow};
    transition: box-shadow 100ms ease-out;

    &:hover {
        box-shadow: ${getBorderBoxShadow},
            ${(props) => (!props.disabled ? '0 8px 24px -2px rgba(25, 54, 67, 0.2)' : null)};

        ${IconWrapper} {
            color: ${(props) => (!props.disabled && !props.selected ? theme.colors.foreground.default : null)};
        }
    }
`;
export const SelectBox = ({ selected, disabled, children, onClick, padding = '16px', ...rest }: SelectBoxProps) => {
    return (
        <ClickableBox
            selected={selected}
            disabled={disabled}
            onClick={disabled ? undefined : onClick}
            padding={padding}
            {...rest}
        >
            <IconWrapper selected={selected} disabled={disabled}>
                {selected ? <IconCircleCheck width={28} height={28} /> : <IconCircle width={28} height={28} />}
            </IconWrapper>

            {children}
        </ClickableBox>
    );
};
